"use client";

import {
  File,
  FileText,
  BookOpen,
  FileType,
  Check,
  Globe,
  Video,
  Image,
  AudioLines,
  MoreVertical,
  ExternalLink,
  Trash2,
  PencilLine,
  Presentation,
  Table,
  FileSpreadsheet,
  Link,
} from "lucide-react";
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
} from "@/components/ui/sidebar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { StudyDocument } from "../../types";
import { useUserLimits, formatFileSize } from "../../hooks/useUserLimits";
import { IconCanvasLogoSolid } from "@instructure/ui-icons";
import { useState } from "react";
import { useNotebookContext } from "../../hooks/useNotebook";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { getSourceIconColored } from "./course-sources-table-b";

interface CourseSourceListProps {
  title: string;
  sources: StudyDocument[];
  selectedSources: StudyDocument[];
  isCourse?: boolean;
  openedSource?: StudyDocument;
  onSourceToggle: (document: StudyDocument) => void;
  onSourceOpen?: (document: StudyDocument) => void;
  onSourceRemove?: (document: StudyDocument) => void;
  onSourceRename?: (document: StudyDocument) => void;
}

export const getSourceIcon = (mime: string) => {
  const lower = mime.toLowerCase();

  // --- Documents ---
  if (lower.includes("pdf")) return FileText;
  if (
    lower.includes("powerpoint") ||
    lower.includes("presentation") ||
    lower.includes("ppt")
  )
    return Presentation;
  if (lower.includes("word") || lower.includes("doc")) return FileText;
  if (
    lower.includes("excel") ||
    lower.includes("spreadsheet") ||
    lower.includes("xls")
  )
    return FileSpreadsheet;

  if (lower.includes("url")) return Link;

  // --- Web / links ---
  if (
    lower.includes("text/html") ||
    lower.includes("website") ||
    lower.includes("http")
  )
    return Globe;

  // --- Video ---
  if (
    lower.startsWith("video/") ||
    lower.includes("mp4") ||
    lower.includes("webm")
  )
    return Video;

  // --- Audio ---
  if (
    lower.startsWith("audio/") ||
    lower.includes("mp3") ||
    lower.includes("wav")
  )
    return AudioLines;

  if (
    lower.includes("text") ||
    lower.includes("markdown") ||
    lower.includes("md")
  )
    return File;

  // --- Images ---
  if (
    lower.startsWith("image/") ||
    lower.includes("png") ||
    lower.includes("jpg") ||
    lower.includes("jpeg")
  )
    return Image;

  // --- Fallback ---
  return BookOpen;
};

export function CourseSourceList({
  title,
  sources,
  selectedSources,
  openedSource,
  onSourceToggle,
  onSourceOpen,
  onSourceRemove,
  onSourceRename,
  isCourse = false,
}: CourseSourceListProps) {
  const { validators, limits } = useUserLimits();
  const [hoveredSource, setHoveredSource] = useState<string | null>(null);
  const [renamingSourceId, setRenamingSourceId] = useState<string | null>(null);
  const [draftName, setDraftName] = useState("");

  const isFileTooLarge = (source: StudyDocument): boolean => {
    return false;
  };

  const selectableSources = sources.filter((s) => !isFileTooLarge(s));

  const allSelected =
    selectableSources.length > 0 &&
    selectableSources.every((s) => selectedSources.includes(s));

  const toggleAll = () => {
    if (allSelected) {
      selectableSources.forEach((s) => {
        if (selectedSources.includes(s)) {
          onSourceToggle(s);
        }
      });
    } else {
      selectableSources.forEach((s) => {
        if (!selectedSources.includes(s)) {
          onSourceToggle(s);
        }
      });
    }
  };

  return (
    <SidebarGroup>
      <div className="flex items-center justify-between">
        <SidebarGroupLabel className="flex items-center gap-1 font-bold">
          {isCourse && <IconCanvasLogoSolid />}
          {title}
        </SidebarGroupLabel>

        {sources.length > 0 && (
          <button
            onClick={toggleAll}
            className="text-xs text-theme-1 hover:text-theme-0 transition"
          >
            {allSelected ? "Deselect all" : "Select all"}
          </button>
        )}
      </div>

      <SidebarGroupContent>
        <SidebarMenu>
          {sources.length === 0 ? (
            <div className="text-theme-1 px-2 py-4 text-sm group-data-[collapsible=icon]:hidden">
              None
            </div>
          ) : (
            sources
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((source) => {
                const sourceId = "id" in source ? source.id : source.name;
                const mime =
                  "file" in source
                    ? source.file.type
                    : typeof source.metadata["content_type"] === "string"
                    ? source.metadata["content_type"]
                    : "";
                const isSelected = selectedSources.includes(source);
                const Icon = getSourceIconColored(
                  "url" in source.metadata ? "url" : mime,
                  isSelected
                );
                const isTooLarge = isFileTooLarge(source);
                const isHovered = hoveredSource === sourceId;

                return (
                  <SidebarMenuItem key={sourceId}>
                    <TooltipProvider delayDuration={200}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div
                            className="hidden group-data-[collapsible=icon]:block"
                            onClick={(e) => {
                              e.stopPropagation();
                              onSourceOpen?.(source);
                            }}
                          >
                            <div
                              className={cn(
                                "relative flex p-1 shrink-0 items-center justify-center rounded-lg cursor-pointer transition-colors",
                                "bg-theme-0 border-theme hover:bg-theme-2",
                                openedSource?.id == source.id && "bg-theme-2"
                              )}
                            >
                              <Icon
                                className={cn("size-6", "text-[7px]")}
                                iconClassName="size-4"
                              />
                            </div>
                          </div>
                        </TooltipTrigger>

                        <TooltipContent side="right" align="center">
                          <span className="text-xs">{source.name}</span>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                    <div
                      className={cn(
                        "group flex items-center gap-3 rounded-md mx-1 px-2 py-0.5 group-data-[collapsible=icon]:hidden",
                        "hover:bg-theme-1 transition-colors cursor-pointer",
                        isSelected && "",
                        openedSource?.id == source.id && "bg-theme-2",
                        openedSource &&
                          openedSource?.id != source.id &&
                          "opacity-50"
                      )}
                      onMouseEnter={() => setHoveredSource(sourceId)}
                      onMouseLeave={() => setHoveredSource(null)}
                      onClick={() => onSourceOpen?.(source)}
                    >
                      {/* Icon or three dots */}
                      <div className="size-4 shrink-0 flex items-center justify-center">
                        <DropdownMenu>
                          <DropdownMenuTrigger
                            onClick={(e) => e.stopPropagation()}
                            className="text-theme-1 hover:bg-theme-2 rounded-full p-2 transition-colors"
                          >
                            {isHovered ? (
                              <MoreVertical className="size-4" />
                            ) : (
                              <Icon
                                className="size-5 text-[7px]"
                                iconClassName="size-3"
                              />
                            )}
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="start" className="w-40">
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                onSourceOpen?.(source);
                              }}
                              className="gap-2 cursor-pointer"
                            >
                              <ExternalLink className="size-4" />
                              Open
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                setRenamingSourceId(sourceId);
                                setDraftName(source.name);
                              }}
                              className="gap-2 cursor-pointer"
                            >
                              <PencilLine className="size-4" />
                              Rename
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                onSourceRemove?.(source);
                              }}
                              className="gap-2 cursor-pointer text-red-600 focus:text-red-600"
                            >
                              <Trash2 className="size-4" />
                              Remove
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>

                      {/* Name */}
                      {renamingSourceId === sourceId ? (
                        <input
                          autoFocus
                          value={draftName}
                          onChange={(e) => setDraftName(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          onBlur={() => {
                            const trimmed = draftName.trim();
                            if (trimmed && trimmed !== source.name) {
                              onSourceRename?.({
                                ...source,
                                name: trimmed,
                              });
                            }
                            setRenamingSourceId(null);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.currentTarget.blur();
                            }
                            if (e.key === "Escape") {
                              setRenamingSourceId(null);
                              setDraftName(source.name);
                            }
                          }}
                          className="flex-1 bg-theme-0 border border-theme rounded px-2 py-0.5 text-sm text-theme-0 focus:outline-none"
                        />
                      ) : (
                        <span
                          className={cn(
                            "flex-1 min-w-0 truncate text-left text-theme-0 text-sm",
                            !isSelected && "text-theme-2"
                          )}
                        >
                          {source.name}
                        </span>
                      )}

                      {/* Checkbox */}
                      <div
                        className="hover:bg-blue-300 transition rounded-full p-1"
                        onClick={(e) => {
                          e.stopPropagation();
                          onSourceToggle(source);
                        }}
                      >
                        <div
                          className={cn(
                            "relative flex size-5 shrink-0 items-center justify-center rounded transition-colors cursor-pointer transition",
                            isSelected
                              ? "bg-slate-600/50"
                              : "bg-theme-0 border-theme border-2 hover:border-theme-1",
                            isTooLarge && "bg-transparent cursor-not-allowed"
                          )}
                        >
                          {isSelected && (
                            <Check className="size-3.5 text-white stroke-[3]" />
                          )}
                        </div>
                      </div>
                    </div>
                  </SidebarMenuItem>
                );
              })
          )}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  );
}
